import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAccountType, AccountType } from '../contexts/AccountTypeContext';
import { AIStrategyPersistenceService, AIStrategyConfig } from '../services/AIStrategyPersistenceService';

export interface UseAIStrategyPersistenceReturn {
  // State
  activeStrategies: { [symbol: string]: AIStrategyConfig };
  isAITradingActive: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  saveStrategy: (symbol: string, strategyConfig: AIStrategyConfig) => Promise<void>;
  loadStrategy: (symbol: string) => Promise<AIStrategyConfig | null>;
  deactivateStrategy: (symbol: string) => Promise<void>;
  loadAllStrategies: () => Promise<void>;
  clearAllStrategies: () => Promise<void>;
  updateGlobalSettings: (settings: any) => Promise<void>;
  
  // Utilities
  getActiveSymbols: () => string[];
  isStrategyActive: (symbol: string) => boolean;
  getStrategyForSymbol: (symbol: string) => AIStrategyConfig | null;
}

export const useAIStrategyPersistence = (): UseAIStrategyPersistenceReturn => {
  const { user } = useAuth();
  const { accountType } = useAccountType();
  
  const [activeStrategies, setActiveStrategies] = useState<{ [symbol: string]: AIStrategyConfig }>({});
  const [isAITradingActive, setIsAITradingActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  /**
   * Save AI strategy configuration
   */
  const saveStrategy = useCallback(async (symbol: string, strategyConfig: AIStrategyConfig) => {
    if (!user) {
      setError('User not authenticated');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      await AIStrategyPersistenceService.saveActiveStrategy(
        user.uid,
        accountType,
        symbol,
        strategyConfig
      );
      
      // Update local state
      setActiveStrategies(prev => ({
        ...prev,
        [symbol]: strategyConfig
      }));
      
      setIsAITradingActive(true);
      
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save strategy';
      setError(errorMessage);
      console.error(`❌ Error saving strategy for ${symbol}:`, err);
    } finally {
      setIsLoading(false);
    }
  }, [user, accountType]);
  
  /**
   * Load AI strategy configuration for a symbol
   */
  const loadStrategy = useCallback(async (symbol: string): Promise<AIStrategyConfig | null> => {
    if (!user) {
      setError('User not authenticated');
      return null;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      const strategyConfig = await AIStrategyPersistenceService.loadActiveStrategy(
        user.uid,
        accountType,
        symbol
      );
      
      if (strategyConfig) {
        // Update local state
        setActiveStrategies(prev => ({
          ...prev,
          [symbol]: strategyConfig
        }));
        
      }
      
      return strategyConfig;
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load strategy';
      setError(errorMessage);
      console.error(`❌ Error loading strategy for ${symbol}:`, err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [user, accountType]);
  
  /**
   * Deactivate AI strategy for a symbol
   */
  const deactivateStrategy = useCallback(async (symbol: string) => {
    if (!user) {
      setError('User not authenticated');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      await AIStrategyPersistenceService.deactivateStrategy(
        user.uid,
        accountType,
        symbol
      );
      
      // Update local state
      setActiveStrategies(prev => {
        const newStrategies = { ...prev };
        delete newStrategies[symbol];
        return newStrategies;
      });
      
      // Check if any strategies are still active
      const remainingStrategies = Object.keys(activeStrategies).filter(s => s !== symbol);
      setIsAITradingActive(remainingStrategies.length > 0);
      
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to deactivate strategy';
      setError(errorMessage);
      console.error(`❌ Error deactivating strategy for ${symbol}:`, err);
    } finally {
      setIsLoading(false);
    }
  }, [user, accountType, activeStrategies]);
  
  /**
   * Load all active strategies
   */
  const loadAllStrategies = useCallback(async () => {
    if (!user) {
      setError('User not authenticated');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      
      const strategies = await AIStrategyPersistenceService.loadAllActiveStrategies(
        user.uid,
        accountType
      );
      
      
      // Convert array to object keyed by symbol
      const strategiesMap: { [symbol: string]: AIStrategyConfig } = {};
      strategies.forEach(strategy => {
        strategiesMap[strategy.symbol] = strategy;
      });
      
      setActiveStrategies(strategiesMap);
      setIsAITradingActive(strategies.length > 0);
      
      //   isAITradingActive: strategies.length > 0
      // });
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load strategies';
      setError(errorMessage);
      console.error(`❌ Error loading all strategies:`, err);
    } finally {
      setIsLoading(false);
    }
  }, [user, accountType]);
  
  /**
   * Clear all AI strategies
   */
  const clearAllStrategies = useCallback(async () => {
    if (!user) {
      setError('User not authenticated');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      await AIStrategyPersistenceService.clearAllStrategies(
        user.uid,
        accountType
      );
      
      setActiveStrategies({});
      setIsAITradingActive(false);
      
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to clear strategies';
      setError(errorMessage);
      console.error(`❌ Error clearing all strategies:`, err);
    } finally {
      setIsLoading(false);
    }
  }, [user, accountType]);
  
  /**
   * Update global AI trading settings
   */
  const updateGlobalSettings = useCallback(async (settings: any) => {
    if (!user) {
      setError('User not authenticated');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      await AIStrategyPersistenceService.updateGlobalSettings(
        user.uid,
        accountType,
        settings
      );
      
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update settings';
      setError(errorMessage);
      console.error(`❌ Error updating global settings:`, err);
    } finally {
      setIsLoading(false);
    }
  }, [user, accountType]);
  
  /**
   * Get all active symbols
   */
  const getActiveSymbols = useCallback((): string[] => {
    return Object.keys(activeStrategies);
  }, [activeStrategies]);
  
  /**
   * Check if strategy is active for a symbol
   */
  const isStrategyActive = useCallback((symbol: string): boolean => {
    return activeStrategies[symbol]?.isActive || false;
  }, [activeStrategies]);
  
  /**
   * Get strategy configuration for a symbol
   */
  const getStrategyForSymbol = useCallback((symbol: string): AIStrategyConfig | null => {
    return activeStrategies[symbol] || null;
  }, [activeStrategies]);
  
  /**
   * Load all strategies on mount and when account type changes
   */
  useEffect(() => {
    if (user) {
      loadAllStrategies();
    }
  }, [user, accountType, loadAllStrategies]);
  
  return {
    // State
    activeStrategies,
    isAITradingActive,
    isLoading,
    error,
    
    // Actions
    saveStrategy,
    loadStrategy,
    deactivateStrategy,
    loadAllStrategies,
    clearAllStrategies,
    updateGlobalSettings,
    
    // Utilities
    getActiveSymbols,
    isStrategyActive,
    getStrategyForSymbol
  };
};
